<template>
  <!-- External Redirect Modal - Estilo Airbnb limpio -->
  <div v-if="isVisible" class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[200]" @click.self="$emit('close')">
    <div class="bg-white w-full sm:max-w-lg sm:mx-4 sm:rounded-2xl rounded-t-2xl shadow-xl overflow-hidden animate-modal-in max-h-[90vh] flex flex-col">
      <!-- Header limpio con imagen del coliving -->
      <div class="relative h-48 sm:h-56 bg-gray-100 flex-shrink-0">
        <!-- Imagen de fondo si existe -->
        <div 
          v-if="firstImageUrl"
          class="absolute inset-0 bg-cover bg-center"
          :style="{ backgroundImage: `url('${firstImageUrl}')` }"
        ></div>
        <!-- Overlay sutil -->
        <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        
        <!-- Botón cerrar estilo Airbnb -->
        <button 
          @click="$emit('close')"
          class="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md"
        >
          <svg class="h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
        
        <!-- Badge del proveedor más sutil -->
        <div class="absolute top-3 left-3">
          <span class="px-2.5 py-1 bg-white text-gray-700 text-xs font-medium rounded-lg shadow-sm">
            {{ providerName }}
          </span>
        </div>
        
        <!-- Título y ubicación sobre la imagen -->
        <div class="absolute bottom-0 left-0 right-0 px-4 pb-4">
          <h3 class="text-base sm:text-lg font-semibold text-white mb-1 line-clamp-1">{{ contract.title }}</h3>
          <p class="text-white/90 text-sm flex items-center">
            <svg class="h-4 w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            </svg>
            {{ contract.propertyLocation }}
          </p>
        </div>
      </div>
      
      <!-- Contenido del modal con scroll -->
      <div class="px-4 py-5 sm:p-6 overflow-y-auto flex-1">
        <!-- Precio y habitaciones -->
        <div class="flex items-center justify-between mb-6 pb-5 border-b border-gray-200">
          <div>
            <p class="text-xs text-gray-500 mb-1">Precio desde</p>
            <p class="text-2xl font-semibold text-gray-900">{{ contract.priceFrom || contract.propertyPrice }}{{ currencySymbol }}<span class="text-sm font-normal text-gray-600">/mes</span></p>
          </div>
          <div class="text-right">
            <p class="text-xs text-gray-500 mb-1">Habitaciones</p>
            <p class="text-lg font-semibold text-gray-900">{{ contract.totalRooms || '—' }}</p>
          </div>
        </div>
      
        <!-- Servicios incluidos (si existen) -->
        <div v-if="contract.servicesInternet || contract.servicesCleaning || contract.minimumStayMonths" class="mb-5">
          <h4 class="text-sm font-semibold text-gray-900 mb-3">Servicios incluidos</h4>
          <div class="flex flex-wrap gap-2">
            <span v-if="contract.servicesInternet" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
              WiFi
            </span>
            <span v-if="contract.servicesCleaning" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
              Limpieza
            </span>
            <span v-if="contract.servicesElectricity && contract.servicesWaterAndWaste" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
              Gastos incluidos
            </span>
            <span v-if="contract.minimumStayMonths" class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg">
              Mín. {{ contract.minimumStayMonths }} {{ contract.minimumStayMonths === 1 ? 'mes' : 'meses' }}
            </span>
          </div>
        </div>
      
        <!-- Estadísticas comparativas de mercado -->
        <div v-if="contract.marketComparison" class="mb-5">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-sm font-semibold text-gray-900">Comparativa de mercado</h4>
            <span class="text-xs text-gray-500">{{ contract.marketComparison.marketContext?.totalInLocation || 0 }} colivings</span>
          </div>
      
          <!-- Grid de 2 columnas más espaciado -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- Precio -->
            <div class="flex items-start gap-3">
              <div class="text-2xl">💰</div>
              <div class="flex-1 min-w-0">
                <p class="text-xs text-gray-500 mb-0.5">Precio</p>
                <p class="text-sm font-medium text-gray-900 truncate">{{ getMarketComparisonTitle(contract.marketComparison.priceRank) }}</p>
              </div>
            </div>
            
            <!-- Centralidad (si existe) -->
            <div v-if="contract.marketComparison.centralityRank" class="flex items-start gap-3">
              <div class="text-2xl">📍</div>
              <div class="flex-1 min-w-0">
                <p class="text-xs text-gray-500 mb-0.5">Ubicación</p>
                <p class="text-sm font-medium text-gray-900 truncate">{{ getCentralityTitle(contract.marketComparison.centralityRank) }}</p>
              </div>
            </div>
            
            <!-- Servicios -->
            <div class="flex items-start gap-3">
              <div class="text-2xl">✨</div>
              <div class="flex-1 min-w-0">
                <p class="text-xs text-gray-500 mb-0.5">Servicios</p>
                <p class="text-sm font-medium text-gray-900 truncate">{{ getServicesTitle(contract.marketComparison.servicesRank) }}</p>
              </div>
            </div>
            
            <!-- Flexibilidad -->
            <div class="flex items-start gap-3">
              <div class="text-2xl">📅</div>
              <div class="flex-1 min-w-0">
                <p class="text-xs text-gray-500 mb-0.5">Flexibilidad</p>
                <p class="text-sm font-medium text-gray-900 truncate">{{ getFlexibilityTitle(contract.marketComparison.flexibilityRank) }}</p>
              </div>
            </div>
          </div>
          
          <!-- Puntos destacados -->
          <div v-if="contract.marketComparison.highlights && contract.marketComparison.highlights.length > 0" class="mt-4 pt-4 border-t border-gray-200">
            <p class="text-xs text-gray-500 mb-2">Destacados</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="highlight in contract.marketComparison.highlights" :key="highlight"
                    class="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                {{ getHighlightLabel(highlight) }}
              </span>
            </div>
          </div>
        </div>
      
        <!-- Mensaje informativo -->
        <div class="bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
          <p class="text-sm text-gray-600">Serás redirigido a <span class="font-semibold text-gray-900">{{ providerName }}</span> para ver más detalles y hacer la reserva.</p>
        </div>
      </div>
      
      <!-- Footer con botones más grandes y limpios -->
      <div class="px-4 py-4 sm:px-6 border-t border-gray-200 bg-white flex-shrink-0">
        <div class="flex flex-col sm:flex-row gap-3">
          <button 
            @click="$emit('close')" 
            class="px-5 py-3 bg-white border border-gray-300 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Volver
          </button>
          
          <!-- Botón para ver todos los colivings del proveedor -->
          <router-link
            :to="`/encuentratucoliving/${providerSlug}`"
            @click="$emit('close')"
            class="px-5 py-3 bg-white border border-indigo-200 text-indigo-600 rounded-full text-sm font-medium flex items-center justify-center gap-2 hover:bg-indigo-50 hover:shadow-md shadow-sm transition-all"
          >
            
            <span class="hidden sm:inline">Más de {{ providerName }}</span>
            <span class="sm:hidden">Más</span>
          </router-link>
          
          <button 
            @click="$emit('go-to-external')" 
            class="flex-1 px-5 py-3 bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50 hover:shadow-md shadow-sm rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-all"
          >
            <span>Ver en {{ providerName }}</span>
            
          </button>
        </div>
        <!-- Disclaimer -->
        <p class="text-xs text-gray-500 text-center mt-3">
          Coolliving no gestiona esta propiedad
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRegion } from '@/composables/useRegion'

const { regionId } = useRegion()
const currencySymbol = computed(() => {
  if (regionId.value === 'us') return '$'
  if (regionId.value === 'uk') return '£'
  return '€'
})

const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true
  },
  contract: {
    type: Object,
    required: true
  },
  firstImageUrl: {
    type: String,
    default: ''
  },
  providerName: {
    type: String,
    required: true
  },
  providerSlug: {
    type: String,
    required: true
  }
})

defineEmits(['close', 'go-to-external'])

// ========================================
// Funciones para estadísticas de mercado
// ========================================

// Título para la comparativa de precio
const getMarketComparisonTitle = (priceRank) => {
  const titles = {
    'very_cheap': 'Muy económico',
    'cheap': 'Precio competitivo',
    'average': 'Precio medio',
    'expensive': 'Por encima de la media',
    'very_expensive': 'Premium'
  }
  return titles[priceRank] || 'Precio'
}

// Título para la comparativa de centralidad
const getCentralityTitle = (centralityRank) => {
  const titles = {
    'very_central': 'Muy céntrico',
    'central': 'Zona céntrica',
    'peripheral': 'Zona residencial',
    'very_peripheral': 'Afueras'
  }
  return titles[centralityRank] || 'Ubicación'
}

// Título para la comparativa de servicios
const getServicesTitle = (servicesRank) => {
  const titles = {
    'basic': 'Servicios básicos',
    'standard': 'Servicios estándar',
    'complete': 'Muy completo',
    'premium': 'Todo incluido'
  }
  return titles[servicesRank] || 'Servicios'
}

// Título para la comparativa de flexibilidad
const getFlexibilityTitle = (flexibilityRank) => {
  const titles = {
    'very_flexible': 'Muy flexible',
    'flexible': 'Flexible',
    'standard': 'Estándar',
    'rigid': 'Largo plazo'
  }
  return titles[flexibilityRank] || 'Flexibilidad'
}

// Label para los highlights
const getHighlightLabel = (highlight) => {
  const labels = {
    'precio_competitivo': 'Buen precio',
    'ubicacion_centrica': 'Céntrico',
    'servicios_completos': 'Completo',
    'flexible': 'Flexible',
    'espacioso': 'Amplio',
    'permite_mascotas': 'Mascotas',
    'permite_parejas': 'Parejas',
    'gimnasio': 'Gym',
    'piscina': 'Piscina',
    'terraza': 'Terraza'
  }
  return labels[highlight] || highlight
}
</script>

<style scoped>
/* Animación de entrada del modal */
@keyframes modal-in {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-modal-in {
  animation: modal-in 0.2s ease-out forwards;
}
</style>

